import { useMemo } from 'react';

import { categoriesToGroups, type CategoriesToGroupsOptions } from '@/features/categories/utils/categories-to-groups';
import { useCategoryTree } from '@/shared/hooks';
import type { CategoryTree } from '@/shared/types';
import { GroupedSelect } from '../grouped-select/grouped-select';
import type { GroupedSelectItem } from '../grouped-select/types';

export interface CategorySelectProps {
  value: string | null;
  onValueChange: (value: string | null) => void;
  /** Provide categories externally to skip the hook fetch. */
  categories?: CategoryTree[];
  /** Options passed to categoriesToGroups (filterBySplitType, alwaysIncludeId). */
  categoriesToGroupsOptions?: CategoriesToGroupsOptions;
  /** Additional top-level items prepended before the system "Uncategorized" option. */
  topLevelItems?: GroupedSelectItem[];
  /** Show "Uncategorized" top-level item. Default: true */
  showUncategorized?: boolean;
  // Forwarded GroupedSelect props:
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  maxHeight?: number;
  stickyHeaders?: boolean;
  collapsible?: boolean;
  'aria-label'?: string;
}

export function CategorySelect({
  value,
  onValueChange,
  categories: externalCategories,
  categoriesToGroupsOptions,
  topLevelItems: topLevelItemsOverride,
  showUncategorized = true,
  id,
  placeholder = 'Select a category',
  disabled = false,
  className,
  maxHeight,
  stickyHeaders,
  collapsible = true,
  'aria-label': ariaLabel,
}: CategorySelectProps) {
  const { data: fetchedTree, isLoading } = useCategoryTree();


  const { groups, topLevelItems: derivedTopLevelItems } = useMemo(() => {
    const tree = externalCategories ?? fetchedTree ?? [];
    return categoriesToGroups(tree, categoriesToGroupsOptions)
  },
    [externalCategories, fetchedTree, categoriesToGroupsOptions]
  );

  // Resolve topLevelItems: custom overrides first, then system "Uncategorized" if enabled
  const resolvedTopLevelItems = useMemo(() => {
    const items: GroupedSelectItem[] = [...(topLevelItemsOverride ?? [])];
    if (showUncategorized) {
      items.push(...derivedTopLevelItems);
    }
    return items;
  }, [topLevelItemsOverride, showUncategorized, derivedTopLevelItems]);

  return (
    <GroupedSelect
      id={id}
      value={value}
      onValueChange={onValueChange}
      groups={groups}
      topLevelItems={resolvedTopLevelItems}
      placeholder={placeholder}
      disabled={isLoading || disabled}
      className={className}
      maxHeight={maxHeight}
      stickyHeaders={stickyHeaders}
      collapsible={collapsible}
      aria-label={ariaLabel}
    />
  );
}
